import React from 'react';
import PropTypes from 'prop-types';
import { FeedbackMessage } from '@kognitiv/bf-library';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { defineWidthClass, inHotelProfilePage } from '../../shared/utils/UtilsHelper';
import ConnectedRoomCard from '../Room/components/ConnectedRoomCard';
import Filters from './Filters';
import { widgetPoolShape, searchShape, currencyShape } from 'Constants/PropTypes';
import Button from 'Components/Card/Button';

class List extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            roomsListWidthClass: '',
            open: false,
        };

        this.RoomsListRef = React.createRef();
        this.watchWidth = this.watchWidth.bind(this);
        this.showMoreOffers = this.showMoreOffers.bind(this);
    }

    componentDidMount() {
        window.addEventListener('resize', this.watchWidth, false);
        this.renderOffersQuantity();

        // call manually for first time, to set the initial width class
        requestAnimationFrame(() => {
            this.watchWidth();
        });
    }

    componentDidUpdate(prevProps) {
        const { rates } = this.props;
        const { rates: prevRates } = prevProps;

        if (rates.length !== prevRates.length) {
            this.renderOffersQuantity();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.watchWidth, false);
    }

    watchWidth() {
        const roomsListWidth = this.RoomsListRef.current ? this.RoomsListRef.current.clientWidth : 0;

        this.setState({ roomsListWidthClass: defineWidthClass(roomsListWidth) });
    }

    renderOffersQuantity() {
        const { rates, onPropertiesRendered, isOffer } = this.props;
        const list = isOffer ? 'Offer List' : 'Rates List';
        const ratesLength = rates && rates.length;
        if (ratesLength && !inHotelProfilePage()) {
            onPropertiesRendered(list, ratesLength);
        }
    }

    showMoreOffers(isOpen) {
        this.setState({ open: !isOpen });
    }

    render() {
        const {
            currency, isLoading, isOffer, rates, searchResults,
            error, currencyDisplay, configuration, shouldUpgradeTier,
            discount, originalPrice, id, search,
            shouldHideButton, shouldShowLogin, newTabDisabled, country,
            currentLanguage, channelId, propertyCode,
        } = this.props;
        const {
            layout, loadingBlocks, showDescription, showSpecs, showTeaser, showFilters, numberOfOffers,
        } = configuration;

        const { language, checkIn, checkOut } = search;

        const { roomsListWidthClass, open } = this.state;

        const layoutDisplayModeDictionary = {
            1 : 'show-as-list',
            2 : 'show-as-grid',
            3 : 'show-as-list',
            4 : 'show-as-grid',
            5 : 'show-as-list',
            6 : 'show-as-grid',
            7 : 'show-as-list',
            8 : 'show-as-grid',
        };

        const handleSearchButtonClick = async (code, availableDaterange, checkIn, checkOut) => {
            searchResults(id, code, isOffer, newTabDisabled, availableDaterange, checkIn, checkOut);
        };

        let ratesToDisplay = numberOfOffers ? rates.slice(0, numberOfOffers) : rates;
        ratesToDisplay = open ? rates : ratesToDisplay;

        // If isLoading is true, we load an array with the established number of blocks
        const [ cardList ] = (!isLoading && ratesToDisplay)
            ? [ ratesToDisplay, '' ]
            : [ Array(loadingBlocks).fill(), 'kbe-loading-block' ];

        const displayMode = cardList?.length === 1
            ? 'show-as-list'
            : (layoutDisplayModeDictionary[layout] || layoutDisplayModeDictionary[1]);

        if (error) {
            return (
                <FeedbackMessage
                    type="error"
                    message={<FormattedMessage id="bf.error" defaultMessage="bf.error" />}
                    align="center"
                    icon="bf-icon-circle-notice"
                />
            );
        }

        // The initial state of isLoading variable is true, then, if one of these two is true, the server has not sent rates
        if (rates.length > 0 || isLoading) {
            const roomListClassNames = classNames(
                'kbe-rooms-list-wrapper',
                displayMode,
                `layout-${displayMode}`,
                roomsListWidthClass,
            );
            return (
                <>
                    { showFilters ? <Filters isOffer={isOffer} /> : null }
                    <div
                        className={roomListClassNames}
                        ref={this.RoomsListRef}
                        data-lvp-webshop-available="true"
                    >
                        {
                            cardList.map((rate, index) => {
                                const key = `${isOffer ? 'ROOM_CARD_' : 'OFFER_CARD'}_${index}`;
                                return (
                                    <ConnectedRoomCard
                                        key={key}
                                        {...rate}
                                        currency={currency}
                                        language={language}
                                        discount={discount}
                                        originalPrice={originalPrice}
                                        currencyDisplay={currencyDisplay}
                                        showDescription={showDescription}
                                        showSpecs={showSpecs}
                                        showTeaser={showTeaser}
                                        isLoading={isLoading}
                                        shouldUpgradeTier={shouldUpgradeTier}
                                        shouldHideButton={shouldHideButton}
                                        shouldShowLogin={shouldShowLogin}
                                        isOffer={isOffer}
                                        onSearchResults={handleSearchButtonClick}
                                        index={index}
                                        country={country}
                                        propertyCode={propertyCode}
                                        channelId={channelId}
                                        checkIn={checkIn}
                                        checkOut={checkOut}
                                        currentLanguage={currentLanguage}
                                        displayedMode={displayMode}
                                    />
                                );
                            })
                        }
                    </div>
                    { isOffer && rates.length > numberOfOffers ? <Button
                        onClick={() => this.showMoreOffers(open)}
                        className="kbe-offer-list-show-more-button kbe-margin-top"
                    >{ open
                            ? <FormattedMessage id="bf.showLess" defaultMessage="bf.showLess" />
                            : <FormattedMessage id="bf.showMore" defaultMessage="bf.showMore" />}
                    </Button> : null }
                </>
            );
        } else {
            // No rates
            return <FeedbackMessage
                type="info"
                message={<FormattedMessage id="bf.noOffersAvailable" defaultMessage="bf.noOffersAvailable" />}
                align="center"
                icon="bf-icon-circle-notice"
            />;
        }
    }
}

List.defaultProps = {
    configuration     : { layout: 1, loadingBlocks: 5 },
    search            : {},
    rates             : [],
    container         : null,
    loadingBlocks     : 5,
    showDescription   : false,
    showTeaser        : false,
    shouldUpgradeTier : false,
    currencyDisplay   : 'code',
    shouldHideButton  : false,
    isOffer           : false,
    onPropertiesRendered: () => {},
};

List.propTypes = {
    configuration     : PropTypes.shape(widgetPoolShape),
    search            : PropTypes.shape(searchShape),
    isLoading         : PropTypes.bool.isRequired,
    rates             : PropTypes.arrayOf(PropTypes.object),
    currency          : PropTypes.shape(currencyShape).isRequired,
    searchResults     : PropTypes.func.isRequired,
    loadingBlocks     : PropTypes.number,
    showDescription   : PropTypes.bool,
    container         : PropTypes.oneOfType([ PropTypes.instanceOf(Element), PropTypes.object ]),
    showTeaser        : PropTypes.bool,
    shouldUpgradeTier : PropTypes.bool,
    shouldHideButton  : PropTypes.bool,
    currencyDisplay   : PropTypes.string,
    isOffer           : PropTypes.bool,
    currentLanguage   : PropTypes.string,
    channelId         : PropTypes.string,
    propertyCode      : PropTypes.string,
    shouldShowLogin   : PropTypes.bool,
    newTabDisabled    : PropTypes.bool,
    country           : PropTypes.string,
    discount          : PropTypes.number,
    originalPrice     : PropTypes.number,
    id                : PropTypes.string,
    error             : PropTypes.bool,
    onPropertiesRendered: PropTypes.func,
};

export default List;
